import { Fragment, FC } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "components/Buttons/ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import Select from "shared/Select/Select";

export interface ExperienceDateInputProps {
  className?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  minHour?: number;
  maxHour?: number;
  slotMinutes?: number;
}

const ExperienceDateInput: FC<ExperienceDateInputProps> = ({
  className = "flex-1",
  value,
  onChange,
  minHour = 0,
  maxHour = 23,
  slotMinutes = 60,
}) => {
  const selectedDate = value;

  const normalizedMinHour = Math.max(0, Math.min(23, Number(minHour)));
  const normalizedMaxHour = Math.max(
    normalizedMinHour,
    Math.min(23, Number(maxHour)),
  );
  const normalizedSlotMinutes = Math.max(1, Number(slotMinutes) || 60);

  const getTimeOptions = () => {
    const options: {
      label: string;
      value: { hour: number; minute: number };
    }[] = [];

    for (
      let totalMinutes = normalizedMinHour * 60;
      totalMinutes <= normalizedMaxHour * 60;
      totalMinutes += normalizedSlotMinutes
    ) {
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;

      if (hour > normalizedMaxHour) {
        break;
      }

      options.push({
        label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
        value: { hour, minute },
      });
    }

    return options;
  };

  const timeOptions = getTimeOptions();

  const onChangeDate = (date: Date | null) => {
    if (!date) {
      onChange?.(null);
      return;
    }

    const nextDate = new Date(date);

    if (selectedDate) {
      nextDate.setHours(
        selectedDate.getHours(),
        selectedDate.getMinutes(),
        0,
        0,
      );
    } else {
      nextDate.setHours(normalizedMinHour, 0, 0, 0);
    }

    onChange?.(nextDate);
  };

  const onChangeTime = (timeValue: string) => {
    if (!selectedDate) return;

    const [hour, minute] = timeValue.split(":").map(Number);
    const nextDate = new Date(selectedDate);
    nextDate.setHours(hour, minute, 0, 0);
    onChange?.(nextDate);
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span className="block xl:text-lg font-semibold">
            {selectedDate?.toLocaleDateString("it-IT", {
              month: "short",
              day: "2-digit",
            }) || "Add date"}
            {selectedDate && (
              <span className="ml-2 text-base font-medium text-neutral-500 dark:text-neutral-400">
                {selectedDate.toLocaleTimeString("it-IT", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </span>
          <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
            {"Date & time"}
          </span>
        </div>
      </>
    );
  };

  return (
    <Popover className={`ExperienceDateInput z-10 relative flex ${className}`}>
      {({ open }) => (
        <>
          <Popover.Button
            className={`flex-1 flex relative p-3 items-center space-x-3 focus:outline-none ${
              open ? "shadow-lg" : ""
            }`}
          >
            {renderInput()}
            {selectedDate && open && (
              <ClearDataButton onClick={() => onChangeDate(null)} />
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute left-auto xl:-right-10 right-0 z-10 mt-3 top-full w-screen max-w-sm px-4 sm:px-0">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
                <div className="flex flex-col gap-6">
                  <div className="[&_.react-datepicker]:!grid-cols-1">
                  <DatePicker
                    selected={selectedDate}
                    onChange={onChangeDate}
                    monthsShown={1}
                    showPopperArrow={false}
                    inline
                    minDate={new Date()}
                    calendarClassName="w-full"
                    renderCustomHeader={(p) => (
                      <DatePickerCustomHeaderTwoMonth {...p} />
                    )}
                    renderDayContents={(day, date) => (
                      <DatePickerCustomDay dayOfMonth={day} date={date} />
                    )}
                  />
                </div>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-3 text-sm font-medium text-neutral-700 dark:text-neutral-200">
                      <ClockIcon className="w-4 h-4" />
                      <span>Select time</span>
                    </div>
                    <Select
                      value={
                        selectedDate
                          ? `${String(selectedDate.getHours()).padStart(2, "0")}:${String(selectedDate.getMinutes()).padStart(2, "0")}`
                          : ""
                      }
                      onChange={(e) => onChangeTime(e.target.value)}
                      disabled={!selectedDate}
                      sizeClass="h-[50px]"
                    >
                      <option value="" disabled>
                        Select time
                      </option>
                      {timeOptions.map((timeOption) => (
                        <option key={timeOption.label} value={timeOption.label}>
                          {timeOption.label}
                        </option>
                      ))}
                    </Select>
                    <span className="mt-2 block text-xs text-neutral-500 dark:text-neutral-400">
                      Available from{" "}
                      {String(normalizedMinHour).padStart(2, "0")}:00 to{" "}
                      {String(normalizedMaxHour).padStart(2, "0")}:00
                    </span>
                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default ExperienceDateInput;
